import {
  ColumnDef,
  SortingState,
  VisibilityState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { api } from "@vuumly-common/common";
import {
  useConstants,
  useOperatorMode,
  useQuery,
  useTranslate,
  useUserContext
} from "hooks";
import { useEffect, useState } from "react";
import { BaseTable } from "../../Tables";
import { DataTableColumnHeader } from "../../Tables/DataTableColumnHeader";
import { TextH5 } from "../../Typography/Text";
import { cn } from "../../shadcn/lib/utils";
import { BaseCard } from "../index";

interface Props {
  maxRows?: number;
  ignoreOriginIdFilter?: boolean;
}

type Item = {
  start_loc_admin_1_name: string;
  start_loc_admin_2_name?: string;
  end_loc_admin_1_name: string;
  end_loc_admin_2_name?: string;
  ride_count: number;
  total_ride_count: number;
  ride_percentage: number;
  ride_count_without_operator?: number;
  ride_count_operator?: number;
  ride_percentage_without_operator?: number;
  ride_percentage_operator?: number;
  total_ride_count_without_operator?: number;
  total_ride_count_operator?: number;
  deviation_percentage?: number;
};

const emptyArray: any[] = [];

export const RideOriginDestinationCard = (props: Props) => {
  const { ORIGIN_ID_KEY, DESTINATION_ID_KEY } = useConstants();
  const { ignoreOriginIdFilter } = props;

  const translate = useTranslate();

  const { operatorMode } = useOperatorMode();
  const { locale } = useUserContext();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    start_loc_admin_1_name: false,
    start_loc_admin_2_name: true,
    end_loc_admin_1_name: false,
    end_loc_admin_2_name: true,
    ride_count: true,
    ride_percentage: true,
    ride_count_operator: false,
    ride_percentage_without_operator: false,
    ride_percentage_operator: false,
    deviation_percentage: operatorMode,
  });

  useEffect(() => {
    setColumnVisibility((prev) => ({
      ...prev,
      deviation_percentage: operatorMode,
    }));
  }, [operatorMode]);

  const { data, isLoading } = useQuery<Item[]>({
    apiEndpoint: api.Endpoint.OriginDestination,
    ignoreParams: ignoreOriginIdFilter
      ? [ORIGIN_ID_KEY, DESTINATION_ID_KEY]
      : [],
  });

  useEffect(() => {
    setColumnVisibility((prev) => ({
      ...prev,
      ride_percentage_operator: operatorMode,
      deviation_percentage: operatorMode,
    }));
  }, [operatorMode]);

  const columns: ColumnDef<Item>[] = [
    {
      accessorKey: "start_loc_admin_1_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          className="w-[150px]"
          column={column}
          title="Ride origin"
        />
      ),
      enableSorting: true,
      sortingFn: (a, b) => {
        const aa = a.getValue("start_loc_admin_1_name") as string;
        const bb = b.getValue("start_loc_admin_1_name") as string;
        if (aa === bb) return 0;
        if (aa === null || bb === null) return 1;
        return aa.localeCompare(bb);
      },
    },
    {
      accessorKey: "start_loc_admin_2_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          className="w-[150px]"
          column={column}
          title="Ride origin"
        />
      ),
      enableSorting: true,
      sortingFn: (a, b) => {
        const aa = (a.getValue("start_loc_admin_2_name") ||
          a.getValue("start_loc_admin_1_name")) as string;
        const bb = (b.getValue("start_loc_admin_2_name") ||
          b.getValue("start_loc_admin_1_name")) as string;
        if (aa === bb) return 0;
        if (aa === null || bb === null) return 1;
        return aa.localeCompare(bb);
      },
      cell: ({ row }) => {
        return (
          row.getValue("start_loc_admin_2_name") ||
          row.getValue("start_loc_admin_1_name")
        );
      },
    },
    {
      accessorKey: "end_loc_admin_1_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          className="w-[150px]"
          column={column}
          title="Ride destination"
        />
      ),
      enableSorting: true,
      sortingFn: (a, b) => {
        const aa = a.getValue("end_loc_admin_1_name") as string;
        const bb = b.getValue("end_loc_admin_1_name") as string;
        if (aa === bb) return 0;
        if (aa === null || bb === null) return 1;
        return aa.localeCompare(bb);
      },
    },
    {
      accessorKey: "end_loc_admin_2_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          className="w-[150px]"
          column={column}
          title="Ride destination"
        />
      ),
      enableSorting: true,
      sortingFn: (a, b) => {
        const aa = (a.getValue("end_loc_admin_2_name") ||
          a.getValue("end_loc_admin_1_name")) as string;
        const bb = (b.getValue("end_loc_admin_2_name") ||
          b.getValue("end_loc_admin_1_name")) as string;
        if (aa === bb) return 0;
        if (aa === null || bb === null) return 1;
        return aa.localeCompare(bb);
      },
      cell: ({ row }) => {
        return (
          row.getValue("end_loc_admin_2_name") ||
          row.getValue("end_loc_admin_1_name") ||
          "Other"
        );
      },
    },
    {
      accessorKey: "ride_count",
      header: ({ column }) => (
        <DataTableColumnHeader
          className="w-[150px]"
          column={column}
          title="Ride count"
        />
      ),
      cell: ({ row }) => {
        const amount = parseFloat(row.getValue("ride_count"));
        const formatted = new Intl.NumberFormat(locale, {
          notation: "compact",
        }).format(amount);

        return formatted;
      },
      enableSorting: true,
    },

    // {
    //   accessorKey: "ride_count_operator",
    //   header: ({ column }) => (
    //     <DataTableColumnHeader
    //       className="w-[150px]"
    //       column={column}
    //       title="Your ride count"
    //     />
    //   ),
    //   cell: ({ row }) => {
    //     const amount = parseFloat(row.getValue("ride_count_operator"));
    //     const formatted = new Intl.NumberFormat(locale, {
    //       notation: "compact",
    //     }).format(amount);

    //     return formatted;
    //   },
    //   enableSorting: true,
    //   enableHiding: operatorMode,
    // },
    {
      accessorKey: "ride_percentage",
      header: ({ column }) => (
        <DataTableColumnHeader
          className="w-[150px]"
          column={column}
          title="% of all rides"
        />
      ),
      cell: ({ row }) => {
        const value = parseFloat(row.getValue("ride_percentage"));
        return value + "%";
      },
      enableSorting: true,
    },
    // {
    //   accessorKey: "ride_percentage_without_operator",
    //   header: ({ column }) => (
    //     <DataTableColumnHeader
    //       className="w-[200px]"
    //       column={column}
    //       title="ride_percentage_without_operator"
    //     />
    //   ),
    //   enableSorting: true,
    //   enableHiding: operatorMode,
    // },
    {
      accessorKey: "ride_percentage_operator",
      header: ({ column }) => (
        <DataTableColumnHeader
          className="w-[150px]"
          column={column}
          title="% of all your rides"
        />
      ),
      cell: ({ row }) => {
        const value = parseFloat(row.getValue("ride_percentage_operator"));
        return value + "%";
      },
      enableSorting: true,
      enableHiding: operatorMode,
    },
    {
      accessorKey: "deviation_percentage",
      header: ({ column }) => (
        <DataTableColumnHeader
          className="w-[150px]"
          column={column}
          title="Your performance"
        />
      ),
      cell: (cell) => {
        const row = cell.row;
        const amount = parseFloat(row.getValue("deviation_percentage"));
        const formatted = amount > 0 ? `+${amount}%` : `${amount}%`;
        return (
          <div className={cn(amount > 0 ? "text-green-600" : "text-red-600")}>
            {formatted}
          </div>
        );
      },
      meta: {
        sticky: false,
      },
      enableSorting: true,
      enableHiding: operatorMode,
    },
  ];

  const table = useReactTable({
    data: data || emptyArray,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onColumnVisibilityChange: setColumnVisibility,

    state: {
      sorting,
      columnVisibility,
    },
  });

  return (
    <BaseCard
      isLoading={isLoading}
      header={
        <div>
          <TextH5>{translate("rides_origin_destination_card_title")}</TextH5>
          {/* <DataTableViewOptions table={table} /> */}
        </div>
      }
    >
      <BaseTable
        columnVisibility={columnVisibility}
        columns={columns}
        table={table}
        paginationProps={{
          enabled: true,
          isRowCountPerPageVisible: false,
          table,
        }}
      />
    </BaseCard>
  );
};
